import type {
  CategoryContentResponseModel,
  ProductTagFacetDto,
  SubCategoryContentResponseModel,
} from '@/services/platform/api/model';
import { cn, useClientTranslation } from '@/lib/core';
import { Icon } from '@/components/ui/icon';

export function ShopProductListTabBar({
  category,
  subCategory,
  categories,
  subcategories,
  subSubcategories,
  activeTabId,
  onTabChange,
  onOpenFilter,
  activeFilterCount = 0,
}: {
  category?: CategoryContentResponseModel;
  subCategory?: SubCategoryContentResponseModel;
  categories?: ProductTagFacetDto[] | null;
  subcategories?: ProductTagFacetDto[] | null;
  subSubcategories?: ProductTagFacetDto[] | null;
  activeTabId: string;
  onTabChange: (id: string) => void;
  onOpenFilter: () => void;
  activeFilterCount?: number;
}) {
  const t = useClientTranslation();

  const allLabel = [t('App.Labels.All'), subCategory?.name ?? category?.name].filter(Boolean).join(' ');

  const tabItems = subSubcategories?.length
    ? subSubcategories
    : subcategories?.length
      ? subcategories
      : (categories ?? []);

  const tabs = [
    { id: 'all', label: allLabel },
    ...tabItems.map(item => ({ id: item.slug ?? item.name ?? '', label: item.name ?? '' })),
  ];

  return (
    <div className="border-jncquoi-neutral-200 flex flex-col items-start border-b bg-white px-4">
      <div className="flex h-9 w-full items-center gap-2">
        <button
          type="button"
          onClick={onOpenFilter}
          className="flex shrink-0 items-center justify-center gap-1 py-2 pr-2"
        >
          <Icon name="filter-list" width={24} height={24} className="text-jncquoi-neutral-1000" />
          {activeFilterCount > 0 && (
            <span className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[12px] tracking-[0.5px] uppercase">
              ({activeFilterCount})
            </span>
          )}
        </button>
        <div className="min-w-px flex-1 scrollbar-none items-center overflow-x-auto overflow-y-hidden">
          <div className="flex shrink-0 items-center gap-4 pr-4">
            {tabs.map(tab => {
              const isActive = tab.id === activeTabId;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => onTabChange(tab.id)}
                  className="flex min-h-9 min-w-9 shrink-0 flex-col items-center justify-center gap-0.5"
                >
                  <span
                    className={cn(
                      'font-pp-neue-montreal text-jncquoi-neutral-1000 text-center text-[14px] leading-none tracking-[0.5px] whitespace-nowrap uppercase',
                      isActive ? 'font-semibold' : 'font-normal'
                    )}
                  >
                    {tab.label}
                  </span>
                  <span
                    className={cn(
                      'h-px w-4 shrink-0',
                      isActive ? 'bg-jncquoi-neutral-1000' : 'bg-transparent'
                    )}
                    aria-hidden="true"
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
